/**
 * One history entry that is cut off after a few lines and reads on in place.
 *
 * Every entry decides for itself: the whole text is in the document from the
 * start - the card was primed with it, or it arrived with the entries somebody
 * stepped back to - so both reading on and folding up again are a class being
 * dropped or set, never a request.
 *
 * Where an entry is on screen twice - the agenda list and the beamer stage show
 * the same point at once - both copies share one answer to "is this being read in
 * full", so switching modes never folds a text back up under the reader.
 *
 * The cut is CSS (`line-clamp-3`) and is already applied by the server, so
 * nothing flashes at full height before Alpine has looked at it. Where little
 * enough stays hidden, the cut is dropped instead and the entry stands there in
 * full: the "read on" button takes a line of its own, so an entry that hides one
 * more line gains nothing by being cut - and clicking it looks like it did
 * nothing at all.
 */
// Which entries are being read in full, kept OUTSIDE the components: the meeting
// editor renders a point in the agenda list and on the beamer stage at the same
// time, and the entries are re-rendered from the server whenever a card steps
// further back. Both copies read this one map, so "read on" is a property of the
// entry - not of the copy of it somebody happened to click.
const expandedEntries = new Map()
// Every instance gets a number, so a broadcast can be told from its own echo.
let excerptInstances = 0

export default ({ syncKey = null } = {}) => {
  // In the closure, never in the component's own state: read back out of Alpine's
  // reactive proxy a native observer loses its `this` and throws on use.
  let stopWatchingLayout = null
  const instanceId = ++excerptInstances

  return {
    // Whether the measurement has happened yet. Until it has, the entry stays cut
    // off exactly as the server rendered it - that is not cosmetics: the overflow
    // can only be read off a box that is still clamped, and dropping the class
    // first would measure an unclamped box, find no overflow and never cut off
    // anything again.
    measured: false,
    // Whether cutting this text off is worth it at all.
    clampable: false,
    expanded: syncKey !== null && expandedEntries.get(syncKey) === true,

    init() {
      // After the paint: a text that has not been laid out yet reports no
      // overflow, and web fonts can still change where the third line ends.
      this.$nextTick(() => this.measure())
    },

    destroy() {
      stopWatchingLayout?.()
      stopWatchingLayout = null
    },

    get clamped() {
      return (!this.measured || this.clampable) && !this.expanded
    },

    toggle() {
      this.setExpanded(!this.expanded)

      if (syncKey === null) return

      window.dispatchEvent(new CustomEvent('point-excerpt-synced', {
        detail: { key: syncKey, origin: instanceId, expanded: this.expanded }
      }))
    },

    /** Follow the other copy of this entry. Nothing to fetch - the text is here. */
    adoptExpanded(detail = {}) {
      if (syncKey === null || detail.key !== syncKey || detail.origin === instanceId) return

      this.setExpanded(detail.expanded === true)
    },

    setExpanded(expanded) {
      this.expanded = expanded

      if (syncKey !== null) expandedEntries.set(syncKey, expanded)
    },

    measure() {
      const text = this.$refs.text

      // No layout, no statement: an entry that is rendered hidden - a closed tab, a
      // point on the beamer stage that is not the one being shown - reports zero
      // for both heights. It ends up uncut, which is the harmless way to be wrong,
      // and the statement is made properly as soon as the entry has a box: without
      // that, an entry only ever rendered hidden first would stand at full length
      // for good, and the "read on" button would never appear.
      if (text && text.clientHeight) {
        stopWatchingLayout?.()
        stopWatchingLayout = null
        this.clampable = text.scrollHeight - text.clientHeight >= this.threshold(text)
      } else {
        this.watchForLayout(text)
      }

      this.measured = true
    },

    /**
     * Wait for the entry to get a box, then measure it for real.
     *
     * A `ResizeObserver` reports the very moment a hidden element is shown, which
     * is precisely when the heights start saying something. A browser without one
     * simply keeps the uncut fallback.
     */
    watchForLayout(text) {
      if (!text || stopWatchingLayout || typeof window.ResizeObserver !== 'function') return

      const observer = new window.ResizeObserver(() => {
        if (!text.clientHeight) return

        this.remeasure()
      })

      observer.observe(text)
      stopWatchingLayout = () => observer.disconnect()
    },

    /**
     * Measure an entry that has meanwhile been laid out. The cut has to go back on
     * first: overflow can only be read off a box that is still cut, and this entry
     * is standing at full length exactly because the first measurement had nothing
     * to look at.
     */
    remeasure() {
      this.measured = false
      this.$nextTick(() => this.measure())
    },

    /**
     * How much has to stay hidden before cutting off pays for itself: a whole line
     * more than the button underneath occupies. Three lines plus a button are as
     * tall as four lines of text, so a fourth line is better simply shown - and
     * everything below that threshold would be a click that changes nothing.
     */
    threshold(text) {
      const lineHeight = parseFloat(window.getComputedStyle(text).lineHeight)
      const line = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 20

      return line + 20
    }
  }
}
